import * as Keychain from 'react-native-keychain';

import { createLogger } from '../../utils/logger';

const logger = createLogger('SecureStorage');

/**
 * Every key the app writes to the keychain MUST be registered here. The
 * registry is enumerated on logout / account wipe so no service can be
 * silently left behind. Keep service names namespaced under `myfinalkey.*`
 * so they don't collide with other apps on the device.
 *
 * NOTE: `userSalt`, `biometricCredentials`, and `masterRecoveryKey` are owned
 * by their respective modules (`UserSalt.ts`, `biometricService.ts`,
 * `documentStorageService.ts`) and accessed directly via `Keychain.*` there.
 * They are listed here so the wipe routine can reach them by name.
 */
export const SecureStorageKeys = {
  authToken: 'myfinalkey.auth_token',
  refreshToken: 'myfinalkey.refresh_token',
  deviceTrustToken: 'myfinalkey.device_trust_token',
  userSalt: 'myfinalkey.user_salt',
  biometricCredentials: 'com.myfinalkey.auth',
  masterRecoveryKey: 'MasterRecoveryKey.myfinalkey',
  // Step 6.2 — client-side rate-limit counters. Stored in the keychain so a
  // user cannot reset the lockout by clearing app data on Android. Each
  // value is a JSON `{ count: number, lastFailureAt: number }`.
  rateLimitLogin: 'myfinalkey.rl.login',
  rateLimitTotp: 'myfinalkey.rl.totp',
  // Medium-term #2 — unwrapped X25519 wrapping secret key, cached after
  // login so consumer flows (successor/keyholder fetch + open a primary's
  // sealed master key) don't need a fresh PBKDF2 every time. Cleared on
  // logout via wipeSessionOnLogout, same lifecycle as masterRecoveryKey.
  wrappingSecretKey: 'WrappingSecretKey.myfinalkey',
  // The account the two cached vault keys above belong to (its user id).
  // They survive an ordinary sign-out for that account only; see
  // services/encryption/vaultKeyCache.ts.
  vaultKeyOwner: 'VaultKeyOwner.myfinalkey',
} as const;

export type SecureStorageKey =
  (typeof SecureStorageKeys)[keyof typeof SecureStorageKeys];

/**
 * Sensitivity determines the keychain access policy.
 *
 *  - `high`  → Biometric prompt required on every access. Use for keys whose
 *              use is deliberate (unlocking the vault, decrypting documents).
 *  - `medium` → No biometric prompt; keychain is unlocked when the device is.
 *              Use for session tokens that the HTTP interceptor reads on
 *              every request — biometry here would prompt continuously.
 */
export type Sensitivity = 'high' | 'medium';

export class SecureStorageError extends Error {
  constructor(message: string) {
    super(`SecureStorage: ${message}`);
    this.name = 'SecureStorageError';
  }
}

// Placeholder username; generic-password needs one but the key is the secret.
const KEYCHAIN_USERNAME = 'myfinalkey';

function accessControlFor(sensitivity: Sensitivity) {
  return sensitivity === 'high'
    ? Keychain.ACCESS_CONTROL.BIOMETRY_CURRENT_SET_OR_DEVICE_PASSCODE
    : undefined;
}

async function writeGenericPassword(
  key: SecureStorageKey,
  value: string,
  options: Keychain.SetOptions,
): Promise<void> {
  const stored = await Keychain.setGenericPassword(
    KEYCHAIN_USERNAME,
    value,
    options,
  );
  if (!stored) {
    throw new SecureStorageError(`failed to persist ${key}`);
  }
}

async function setItem(
  key: SecureStorageKey,
  value: string,
  sensitivity: Sensitivity = 'medium',
): Promise<void> {
  if (typeof value !== 'string') {
    throw new SecureStorageError(`value for ${key} must be a string`);
  }
  try {
    const options: Keychain.SetOptions = {
      service: key,
      accessible: Keychain.ACCESSIBLE.WHEN_UNLOCKED_THIS_DEVICE_ONLY,
    };
    const accessControl = accessControlFor(sensitivity);
    if (accessControl) {
      options.accessControl = accessControl;
    }
    try {
      await writeGenericPassword(key, value, options);
    } catch (firstErr) {
      // A stale access-control entry can wedge a single service key on iOS.
      // Clear only this service and try once more before surfacing failure.
      logger.warn(`setItem(${key}) retrying after keychain write failure`);
      await Keychain.resetGenericPassword({ service: key }).catch(() => {
        logger.warn(`setItem(${key}) reset before retry failed`);
      });
      await writeGenericPassword(key, value, options);
    }
  } catch (err) {
    // Never include the value or native error details in logs.
    logger.error(`setItem(${key}) failed`);
    if (err instanceof SecureStorageError) throw err;
    throw new SecureStorageError(`setItem failed for ${key}`);
  }
}

async function getItem(key: SecureStorageKey): Promise<string | null> {
  try {
    const existing = await Keychain.getGenericPassword({ service: key });
    if (existing && typeof existing !== 'boolean' && existing.password) {
      return existing.password;
    }
    return null;
  } catch (err) {
    // Missing entries are expected and must not throw. Only native-level
    // errors reach here; swallow them so callers never have to special-case
    // "no value stored yet" vs. "keychain read failed".
    logger.debug(`getItem(${key}) returning null (miss or error)`);
    return null;
  }
}

async function removeItem(key: SecureStorageKey): Promise<void> {
  try {
    await Keychain.resetGenericPassword({ service: key });
  } catch (err) {
    logger.error(`removeItem(${key}) failed`);
    // Swallow — we're removing and any future read returns null anyway.
  }
}

async function clear(): Promise<void> {
  const keys = Object.values(SecureStorageKeys) as SecureStorageKey[];
  await Promise.all(keys.map((k) => removeItem(k)));
}

export const SecureStorage = {
  setItem,
  getItem,
  removeItem,
  clear,
};
